<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { Modal } from "bootstrap";

import { Button } from "@/components/global";
import { CircleCloseIcon } from "@/components/svg";

const props = defineProps<{
  showModal: boolean;
  closable?: boolean;
  rounded?: boolean;
  maxWidth?: string;
  header?: string;
  closeType?: "icon" | "text";
}>();

const emit = defineEmits(["closeModal"]);

const showModalRef = ref<HTMLElement | null>(null);
const modalInstance = ref<Modal | null>(null);

watch(() => props.showModal, value => {
  if (modalInstance.value) {
    value ? modalInstance.value.show() : modalInstance.value.hide();
  }
});

onMounted(() => {
  if (showModalRef.value) {
    modalInstance.value = new Modal(showModalRef.value);

    showModalRef.value.addEventListener("hidden.bs.modal", () => {
      emit("closeModal");
    });
  }
});
</script>

<template>
  <div
    class="modal fade p-0"
    ref="showModalRef"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="staticBackdropLabel"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-dialog-centered"
      :style="{ maxWidth }"
    >
      <div class="modal-content w-100 bg-transparent">
        <div
          class="position-relative w-100"
          :style="{ background: 'var(--ch-white)', borderRadius: rounded ? '10px': 0 }"
        >
          <header v-if="header" class="modal-header">
            <h2 class="modal-title">{{ header }}</h2>
          </header>
          <Button
            v-if="closable"
            variant="naked"
            class="close-btn"
            :style="{
              color: 'var(--ch-black)',
              width: closeType === 'text' ? 'auto' : '1.5rem',
              height: closeType === 'text' ? 'auto' : '1.5rem'
            }"
            aria-label="close-modal-btn"
            @click="emit('closeModal')"
            testId="close-modal-btn"
          >
            <span
              v-if="closeType === 'text'"
              style="font-size: var(--font-sm); font-weight: 400; letter-spacing: -3%;"
            >
              Close
            </span>
            <CircleCloseIcon v-else />
          </Button>
          <div class="modal-body" :style="{ padding: header ? '1rem 0 0 0' : 0 }">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-content {
  -webkit-border: none;
  -moz-border: none;
  border: none;
}

.modal-header {
  padding: 0.5rem;
  min-width: 10rem;
  height: 3rem;
  background-color: var(--ch-blue-100);
  color: var(--ch-white);
  border-radius: 5px;
  border-bottom: 0;
  position: absolute;
  z-index: 10;
  top: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
}

.modal-header .modal-title {
  width: 100%;
  font-size: var(--font-sm);
  font-weight: 500;
  text-transform: capitalize;
  line-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  height: 100%;
  max-height: 37.5rem;
  overflow-y: auto;
}

.modal-content .close-btn {
  position: absolute;
  z-index: 10;
  top: 0.5rem;
  right: 0.5rem;
  line-height: 100%;
}

@media screen and (min-width: 576px) {
  .modal-dialog {
    max-width: 32rem;
  }

  .modal-header .modal-title {
    font-size: var(--font-base);
  }
}

@media screen and (min-width: 768px) {
  .modal-dialog {
    max-width: 36rem;
  }

  .modal-header {
    min-width: 12rem;
  }
}

@media screen and (max-height: 40rem) {
  .modal-body {
    max-height: 95vh;
  }
}
</style>
